import { HttpClient } from "@angular/common/http";
import { TranslateLoader, TranslationObject } from "@ngx-translate/core";
import { Observable, forkJoin, of } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { TranslationClient } from "src/api-service";
import { BUILD_TIMESTAMP } from "src/environments/version";

/**
 * Custom TranslateLoader that merges JSON files with database overrides.
 *
 * 1. Loads base translations from assets/i18n/{lang}.json
 * 2. Fetches overrides from API: /Application/Translation/{lang}
 * 3. Merges them (overrides win)
 *
 * If API is unavailable, falls back to JSON only.
 */
export class HybridTranslateLoader implements TranslateLoader {

    constructor(
        private readonly http: HttpClient,
        private readonly translationClient: TranslationClient,
    ) {}

    public getTranslation(lang: string): Observable<TranslationObject> {
        const base$ = this.http.get<TranslationObject>(
            `./assets/i18n/${lang}.json?v=${BUILD_TIMESTAMP}`
        );

        const overrides$ = this.translationClient.getOverridesByLanguage(lang).pipe(
            catchError(() => of({}))
        );

        return forkJoin([base$, overrides$]).pipe(
            map(([base, overrides]) => this.merge(base, overrides))
        );
    }

    private merge(
        base: TranslationObject,
        overrides: Record<string, string>
    ): TranslationObject {
        const result = JSON.parse(JSON.stringify(base));

        for (const [key, value] of Object.entries(overrides)) {
            this.setNested(result, key, value);
        }

        return result;
    }

    private setNested(obj: Record<string, unknown>, path: string, value: string): void {
        // Handle both "quote.status.draft" and "QUOTE.STATUS.DRAFT" formats
        const keys = path.split(".");
        let current = obj;

        for (let i = 0; i < keys.length - 1; i++) {
            // Try exact case first, then uppercase (JSON uses uppercase)
            const key = keys[i];
            const upperKey = key.toUpperCase();

            if (key in current) {
                current = current[key] as Record<string, unknown>;
            } else if (upperKey in current) {
                current = current[upperKey] as Record<string, unknown>;
            } else {
                // Create path if it doesn't exist
                current[upperKey] = {};
                current = current[upperKey] as Record<string, unknown>;
            }
        }

        // Set the final value (try exact case, then uppercase)
        const finalKey = keys[keys.length - 1];
        const upperFinalKey = finalKey.toUpperCase();

        if (finalKey in current) {
            current[finalKey] = value;
        } else {
            current[upperFinalKey] = value;
        }
    }
}
